import { html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { WEB_COMPONENT_GZ_RM_TOP_IDENTITY_RISK_IMPACT_WIDGET } from '../../../../webcomponents.constants';
import { RmTopIdentityRisksByImpactPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { renderNumber, renderNumberWithTooltip, renderText, renderTitle } from '../../text';
import { translateFn } from '../../../../utils/translator.helper';
import { DEFAULT_RM_WIDGET_TOP_ROWS } from '@nirvana/shared/constants';
import { Row, RowConfig } from '../../../../ui/gzBasicTable/types/types';
import { fillArray } from '@nirvana/shared/basic-types/array';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { ColumnKeys as UserBehaviourColumnRisksKeys } from '../../../userBehaviorRisksApp/types';
import { LinkType } from '../../../../ui/gzLinkComponent/types';
import { FontSizes } from '../../../../types/common/types';
import { getRoutinesTranslator } from '../../../common/translations';
import { State } from '../../../common/constants';
import { computeMaxRowsFit, fitItemsToWidget } from '../../resize';
import { renderIdentityMonitoringDisabled } from '../../identity.monitoring';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-top-identity-risks-impact-widget
 */
@customElement(WEB_COMPONENT_GZ_RM_TOP_IDENTITY_RISK_IMPACT_WIDGET)
export class GzRmTopIdentityRisksImpactWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RmTopIdentityRisksByImpactPayload = { topIdentityRisksByImpact: { companyId: '', total: 0, items: [], identityMonitoringDisabled: false } };

  routinesTranslator = getRoutinesTranslator();

  static get styles() {
    return styles;
  }

  protected viewAll(): void {
    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload.topIdentityRisksByImpact?.companyId ?? '' },
      filters: [
        {
          [UserBehaviourColumnRisksKeys.STATE]: { in: [State.ACTIVE] },
        },
        {
          [UserBehaviourColumnRisksKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
        },
      ],
      section: 'identity-risks',
      newTab: true,
    });
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader class="pt-1" .rows="${computeMaxRowsFit(this.contentHeight)}" columns="1"></gz-loader>`;
  }

  protected renderContent(): TemplateResult {
    if (this.payload.topIdentityRisksByImpact?.identityMonitoringDisabled) {
      return renderIdentityMonitoringDisabled();
    }

    const items: Row[] =
      this.payload.topIdentityRisksByImpact?.items?.map(
        ({ indicatorId, affectedUsers, totalAffectedUsers }): RowConfig => ({
          data: { indicatorId },
          cells: [
            renderText(this.routinesTranslator.translateRoutineName(indicatorId, true)),
            html` <gz-rm-icons-list
              iconSet="entity"
              iconClassMapType="users"
              .items="${affectedUsers}"
            ></gz-rm-icons-list>`,
            renderNumberWithTooltip(totalAffectedUsers, translateFn('Total vulnerable identities')),
          ],
        })
      ) ?? [];

    const emptyItem = [renderText('...', true), html` <gz-rm-icons-list></gz-rm-icons-list>`, renderNumber('--', true)];
    const fullItems: Row[] = fitItemsToWidget(fillArray(items, DEFAULT_RM_WIDGET_TOP_ROWS, emptyItem), this.contentHeight);

    return html`
      <gz-basic-table
        style="margin-top: calc(var(--spacer)*0.3)"
        rowsClickable
        alignLastColumnRight
        .onClick="${(rowData: { indicatorId: number }) => {
          if (!rowData) {
            return;
          }

          const { indicatorId } = rowData;

          NavigationHelper.navigateToRiskManagement({
            company: { id: this.payload.topIdentityRisksByImpact?.companyId ?? '' },
            section: 'identity-risks',
            filters: [
              {
                [UserBehaviourColumnRisksKeys.USER_BEHAVIOR_RISK]: {
                  complexEnumFilter: [{ value: indicatorId }],
                },
              },
            ],
            newTab: true,
          });
        }}"
        .data="${fullItems}"
      ></gz-basic-table>

      <gz-link type="${LinkType.QUIET}" fontSize="${FontSizes.PARAGRAPH_11}" .callback=${() => this.viewAll()}>
        ${translateFn('View all')} (${this.payload.topIdentityRisksByImpact?.total ?? 0})
      </gz-link>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-top-identity-risks-impact-widget': GzRmTopIdentityRisksImpactWidgetWebComponent;
  }
}
